import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { PublicConsent } from '@/api/publicEntities';
import MarkdownDocument from '@/components/consent/MarkdownDocument';
import { Button } from '@/components/ui/button';

const TYPE_LABELS = {
  agreement: 'Согласие на обработку персональных данных',
  privacy_policy: 'Политика обработки персональных данных',
  oferta: 'Оферта',
  eula: 'Пользовательское соглашение',
  marketing: 'Согласие на маркетинговые коммуникации'
};

export default function PublicDocumentPage() {
  const { type } = useParams();
  const documentType = useMemo(() => decodeURIComponent(type || ''), [type]);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDoc(null);

    PublicConsent.getCurrent(documentType ? [documentType] : [])
      .then(list => {
        if (cancelled) return;
        const docs = Array.isArray(list) ? list : [];
        setDoc(docs.find(item => item.type === documentType) || docs[0] || null);
      })
      .catch(e => {
        if (!cancelled) setError(e?.message || 'Не удалось загрузить документ');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [documentType]);

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">BookForge</p>
            <h1 className="mt-1 text-3xl font-bold text-slate-950">
              {doc?.name || TYPE_LABELS[documentType] || 'Документ'}
            </h1>
            {doc && (
              <p className="mt-2 text-sm text-slate-600">
                Тип: <code>{doc.type}</code>
                {doc.version ? <> · версия {doc.version}</> : null}
                {doc.effective_from ? <> · действует с {new Date(doc.effective_from).toLocaleString()}</> : null}
              </p>
            )}
          </div>
          <Button variant="outline" asChild>
            <Link to="/">В магазин</Link>
          </Button>
        </div>

        <section className="rounded-lg border bg-white p-5 shadow-sm sm:p-8">
          {loading && (
            <div className="flex items-center text-sm text-slate-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Загрузка документа...
            </div>
          )}

          {!loading && error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && !doc && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              Текущая версия документа не опубликована.
            </div>
          )}

          {doc && <MarkdownDocument content={doc.content} />}
        </section>
      </div>
    </main>
  );
}
